/**
 * Consolidation Wizard (K3 kap 9)
 *
 * Wizard för koncernredovisning med stöd för:
 * - Uppladdning av dotterbolags SIE-filer
 * - Konfiguration av förvärvsdata
 * - Visning av elimineringar och justeringar
 * - Visning av minoritetsintresse
 * - Preview av konsoliderad årsredovisning
 */

import { useState, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import {
  Building2,
  Upload,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  Users,
  Calculator,
  Eye,
  Info,
  FileText,
} from "lucide-react";
import { formatNumber } from "@/lib/format";
import type { AnnualReport, UserInput } from "@/lib/api";

// ═══════════════════════════════════════════════════════════════════════════
// TYPER
// ═══════════════════════════════════════════════════════════════════════════

interface SubsidiaryData {
  id: string;
  name: string;
  organizationNumber: string;
  sieFileUploaded: boolean;
  sieFileName?: string;
  // Acquisition data
  acquisitionDate?: string;
  acquisitionCost?: number;
  equityAtAcquisition?: number;
  ownershipPercentage?: number;
  votingRightsPercentage?: number;
  goodwillUsefulLife?: number;
  // Parsed data (from SIE)
  parsedData?: {
    totalEquity: number;
    currentYearResult: number;
    totalAssets: number;
    netRevenue: number;
  };
}

interface ConsolidationWizardProps {
  report: AnnualReport;
  onUpdate: (report: AnnualReport) => void;
  onClose?: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function ConsolidationWizard({ report, onUpdate: _onUpdate, onClose }: ConsolidationWizardProps) {
  const [step, setStep] = useState(1);
  const [subsidiaries, setSubsidiaries] = useState<SubsidiaryData[]>(() => {
    // Initialize from existing data if available
    const userInput = report.userInput as UserInput;
    if (userInput.subsidiaries && userInput.subsidiaries.length > 0) {
      return userInput.subsidiaries.map((sub) => ({
        id: sub.id,
        name: sub.metadata.name,
        organizationNumber: sub.metadata.organizationNumber,
        sieFileUploaded: true,
        sieFileName: `${sub.metadata.name}.se`,
        acquisitionDate: userInput.consolidation?.subsidiaryInputs?.find(
          (s) => s.subsidiaryId === sub.id
        )?.acquisitionDate,
        acquisitionCost: userInput.consolidation?.subsidiaryInputs?.find(
          (s) => s.subsidiaryId === sub.id
        )?.acquisitionCost,
        equityAtAcquisition: userInput.consolidation?.subsidiaryInputs?.find(
          (s) => s.subsidiaryId === sub.id
        )?.acquisitionCost,
        ownershipPercentage: userInput.consolidation?.subsidiaryInputs?.find(
          (s) => s.subsidiaryId === sub.id
        )?.ownershipPercent,
        votingRightsPercentage: userInput.consolidation?.subsidiaryInputs?.find(
          (s) => s.subsidiaryId === sub.id
        )?.votingRightsPercent,
        goodwillUsefulLife: userInput.consolidation?.subsidiaryInputs?.find(
          (s) => s.subsidiaryId === sub.id
        )?.goodwillUsefulLife,
      }));
    }
    return [];
  });
  const [expandedSubsidiary, setExpandedSubsidiary] = useState<string | null>(null);

  // Parent company info
  const parsedSie = report.parsedSieData;
  const parentCompany = {
    name: parsedSie?.metadata?.companyName || "Moderbolag",
    organizationNumber: parsedSie?.metadata?.organizationNumber || "",
    totalEquity: parsedSie?.balanceSheet?.equityAndLiabilities?.equity?.total || 0,
    totalAssets: parsedSie?.balanceSheet?.assets?.totalAssets || 0,
    netRevenue: report.calculations?.keyFigures?.netSales || 0,
  };

  // Add subsidiary
  const addSubsidiary = () => {
    const newSub: SubsidiaryData = {
      id: `sub-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name: "",
      organizationNumber: "",
      sieFileUploaded: false,
      ownershipPercentage: 100,
      votingRightsPercentage: 100,
      goodwillUsefulLife: 5,
    };
    setSubsidiaries([...subsidiaries, newSub]);
    setExpandedSubsidiary(newSub.id);
  };

  // Remove subsidiary
  const removeSubsidiary = (id: string) => {
    setSubsidiaries(subsidiaries.filter((s) => s.id !== id));
    if (expandedSubsidiary === id) {
      setExpandedSubsidiary(null);
    }
  };

  // Update subsidiary
  const updateSubsidiary = (id: string, updates: Partial<SubsidiaryData>) => {
    setSubsidiaries(
      subsidiaries.map((s) => (s.id === id ? { ...s, ...updates } : s))
    );
  };

  // Handle SIE file drop for a subsidiary
  const handleSieDrop = useCallback(
    (id: string, acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // Simulate parsing SIE file (in real app, send to backend)
        // For now, just mark as uploaded and set dummy data
        updateSubsidiary(id, {
          sieFileUploaded: true,
          sieFileName: file.name,
          // In real implementation, these would come from parsed SIE
          parsedData: {
            totalEquity: 500000,
            currentYearResult: 50000,
            totalAssets: 1000000,
            netRevenue: 2000000,
          },
        });
      }
    },
    [updateSubsidiary]
  );

  // Validation
  const isStepValid = (stepNum: number): boolean => {
    switch (stepNum) {
      case 1:
        // At least one subsidiary with SIE file
        return subsidiaries.length > 0 && subsidiaries.every((s) => s.sieFileUploaded);
      case 2:
        // All subsidiaries have acquisition data
        return subsidiaries.every(
          (s) =>
            s.acquisitionDate &&
            s.acquisitionCost !== undefined &&
            s.ownershipPercentage !== undefined
        );
      case 3:
        // Preview step - always valid
        return true;
      default:
        return false;
    }
  };

  // Calculate minority interest for preview
  const calculateMinorityInterest = () => {
    return subsidiaries
      .filter((s) => (s.ownershipPercentage || 0) < 100 && s.parsedData)
      .map((s) => ({
        name: s.name,
        minorityPercentage: 100 - (s.ownershipPercentage || 0),
        minorityEquity:
          (s.parsedData?.totalEquity || 0) *
          ((100 - (s.ownershipPercentage || 0)) / 100),
        minorityResult:
          (s.parsedData?.currentYearResult || 0) *
          ((100 - (s.ownershipPercentage || 0)) / 100),
      }));
  };

  // Calculate goodwill for preview
  const calculateGoodwill = () => {
    return subsidiaries
      .filter((s) => s.acquisitionCost && s.equityAtAcquisition && s.ownershipPercentage)
      .map((s) => {
        const acquiredEquityShare =
          (s.equityAtAcquisition || 0) * ((s.ownershipPercentage || 0) / 100);
        const goodwillAmount = (s.acquisitionCost || 0) - acquiredEquityShare;
        return {
          name: s.name,
          acquisitionCost: s.acquisitionCost || 0,
          acquiredEquityShare,
          goodwillAmount: Math.max(0, goodwillAmount),
          usefulLife: s.goodwillUsefulLife || 5,
        };
      });
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════

  return (
    <div className="card">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Building2 className="text-indigo-600" size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Koncernredovisning</h2>
            <p className="text-sm text-gray-500">K3 kap 9 - Konsoliderad årsredovisning</p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
          K3
        </span>
      </div>

      {/* Progress steps */}
      <div className="flex items-center justify-between mb-8">
        {[
          { num: 1, title: "Dotterbolag", icon: Users },
          { num: 2, title: "Förvärvsdata", icon: Calculator },
          { num: 3, title: "Förhandsvisning", icon: Eye },
        ].map((s, i) => (
          <div key={s.num} className="flex items-center">
            <div
              className={`flex items-center justify-center w-8 h-8 rounded-full ${
                step >= s.num
                  ? "bg-indigo-600 text-white"
                  : "bg-gray-200 text-gray-500"
              }`}
            >
              {step > s.num ? <CheckCircle size={16} /> : <s.icon size={16} />}
            </div>
            <span
              className={`ml-2 text-sm font-medium ${
                step >= s.num ? "text-gray-900" : "text-gray-500"
              }`}
            >
              {s.title}
            </span>
            {i < 2 && (
              <div
                className={`w-16 h-0.5 mx-4 ${
                  step > s.num ? "bg-indigo-600" : "bg-gray-200"
                }`}
              />
            )}
          </div>
        ))}
      </div>

      {/* Step content */}
      <div className="space-y-6">
        {/* Step 1: Upload subsidiary SIE files */}
        {step === 1 && (
          <>
            {/* Parent company info */}
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Building2 size={16} className="text-indigo-600" />
                <h3 className="font-medium text-indigo-900">Moderbolag</h3>
              </div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <span className="text-indigo-600">Namn:</span>{" "}
                  <span className="font-medium text-indigo-900">{parentCompany.name}</span>
                </div>
                <div>
                  <span className="text-indigo-600">Totala tillgångar:</span>{" "}
                  <span className="font-medium text-indigo-900">
                    {formatNumber(parentCompany.totalAssets)} kr
                  </span>
                </div>
                <div>
                  <span className="text-indigo-600">Eget kapital:</span>{" "}
                  <span className="font-medium text-indigo-900">
                    {formatNumber(parentCompany.totalEquity)} kr
                  </span>
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <div className="flex gap-2">
                <Info size={16} className="text-blue-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-800">
                  Ladda upp SIE-filer för samtliga dotterbolag som ska ingå i koncernredovisningen.
                  Varje dotterbolag behöver en SIE4-fil med komplett bokslut.
                </p>
              </div>
            </div>

            {/* Subsidiaries list */}
            <div className="space-y-3">
              {subsidiaries.map((sub) => (
                <SubsidiaryCard
                  key={sub.id}
                  subsidiary={sub}
                  isExpanded={expandedSubsidiary === sub.id}
                  onToggle={() =>
                    setExpandedSubsidiary(expandedSubsidiary === sub.id ? null : sub.id)
                  }
                  onUpdate={(updates) => updateSubsidiary(sub.id, updates)}
                  onRemove={() => removeSubsidiary(sub.id)}
                  onSieDrop={(files) => handleSieDrop(sub.id, files)}
                  showAcquisitionFields={false}
                />
              ))}

              {subsidiaries.length === 0 && (
                <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                  <Building2 size={32} className="mx-auto mb-2 text-gray-400" />
                  <p>Inga dotterbolag tillagda ännu</p>
                  <p className="text-sm mt-1">Klicka på knappen nedan för att lägga till</p>
                </div>
              )}
            </div>

            {/* Add subsidiary button */}
            <button
              type="button"
              onClick={addSubsidiary}
              className="flex items-center gap-2 w-full justify-center py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-indigo-400 hover:text-indigo-600 transition-colors"
            >
              <Plus size={20} />
              Lägg till dotterbolag
            </button>
          </>
        )}

        {/* Step 2: Acquisition data */}
        {step === 2 && (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex gap-2">
                <Info size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-medium">Ange förvärvsuppgifter för varje dotterbolag</p>
                  <p className="mt-1">
                    Denna information behövs för att beräkna koncerngoodwill och minoritetsintresse.
                    Förvärvsdag och anskaffningsvärde hämtas normalt från avtal eller styrelseprotokoll.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {subsidiaries.map((sub) => (
                <SubsidiaryCard
                  key={sub.id}
                  subsidiary={sub}
                  isExpanded={true}
                  onToggle={() => {}}
                  onUpdate={(updates) => updateSubsidiary(sub.id, updates)}
                  onRemove={() => removeSubsidiary(sub.id)}
                  onSieDrop={() => {}}
                  showAcquisitionFields={true}
                />
              ))}
            </div>
          </>
        )}

        {/* Step 3: Preview */}
        {step === 3 && (
          <>
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <div className="flex gap-2">
                <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-green-800">
                  Granska koncernredovisningens preliminära beräkningar nedan.
                  Den fullständiga konsolideringen sker vid generering av årsredovisningen.
                </p>
              </div>
            </div>

            {/* Consolidated summary */}
            <div className="grid grid-cols-2 gap-4">
              {/* Total assets */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Summerade tillgångar (före eliminering)
                </h4>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(
                    parentCompany.totalAssets +
                      subsidiaries.reduce(
                        (sum, s) => sum + (s.parsedData?.totalAssets || 0),
                        0
                      )
                  )}{" "}
                  kr
                </p>
              </div>

              {/* Total revenue */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-500 mb-2">
                  Summerad nettoomsättning (före eliminering)
                </h4>
                <p className="text-2xl font-bold text-gray-900">
                  {formatNumber(
                    parentCompany.netRevenue +
                      subsidiaries.reduce(
                        (sum, s) => sum + (s.parsedData?.netRevenue || 0),
                        0
                      )
                  )}{" "}
                  kr
                </p>
              </div>
            </div>

            {/* Goodwill */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="font-medium text-gray-900">Koncerngoodwill</h4>
              </div>
              <div className="p-4">
                {calculateGoodwill().length > 0 ? (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="pb-2">Dotterbolag</th>
                        <th className="pb-2 text-right">Anskaffningsvärde</th>
                        <th className="pb-2 text-right">Förvärvat EK-andel</th>
                        <th className="pb-2 text-right">Goodwill</th>
                        <th className="pb-2 text-right">Avskr.tid</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {calculateGoodwill().map((g) => (
                        <tr key={g.name}>
                          <td className="py-2 font-medium text-gray-900">{g.name || "Ej angivet"}</td>
                          <td className="py-2 text-right">{formatNumber(g.acquisitionCost)} kr</td>
                          <td className="py-2 text-right">
                            {formatNumber(g.acquiredEquityShare)} kr
                          </td>
                          <td className="py-2 text-right font-medium">
                            {formatNumber(g.goodwillAmount)} kr
                          </td>
                          <td className="py-2 text-right">{g.usefulLife} år</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-gray-200">
                      <tr className="font-bold">
                        <td className="pt-2">Totalt</td>
                        <td className="pt-2 text-right">-</td>
                        <td className="pt-2 text-right">-</td>
                        <td className="pt-2 text-right">
                          {formatNumber(
                            calculateGoodwill().reduce((sum, g) => sum + g.goodwillAmount, 0)
                          )}{" "}
                          kr
                        </td>
                        <td className="pt-2 text-right">-</td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Ingen koncerngoodwill beräknad. Ange förvärvsdata i föregående steg.
                  </p>
                )}
              </div>
            </div>

            {/* Minority interest */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <h4 className="font-medium text-gray-900">Minoritetsintresse</h4>
              </div>
              <div className="p-4">
                {calculateMinorityInterest().length > 0 ? (
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500">
                        <th className="pb-2">Dotterbolag</th>
                        <th className="pb-2 text-right">Minoritetsandel</th>
                        <th className="pb-2 text-right">Minoritetens EK</th>
                        <th className="pb-2 text-right">Minoritetens resultat</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {calculateMinorityInterest().map((mi) => (
                        <tr key={mi.name}>
                          <td className="py-2 font-medium text-gray-900">{mi.name || "Ej angivet"}</td>
                          <td className="py-2 text-right">{mi.minorityPercentage}%</td>
                          <td className="py-2 text-right">{formatNumber(mi.minorityEquity)} kr</td>
                          <td className="py-2 text-right">{formatNumber(mi.minorityResult)} kr</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="border-t border-gray-200">
                      <tr className="font-bold">
                        <td className="pt-2">Totalt</td>
                        <td className="pt-2 text-right">-</td>
                        <td className="pt-2 text-right">
                          {formatNumber(
                            calculateMinorityInterest().reduce(
                              (sum, mi) => sum + mi.minorityEquity,
                              0
                            )
                          )}{" "}
                          kr
                        </td>
                        <td className="pt-2 text-right">
                          {formatNumber(
                            calculateMinorityInterest().reduce(
                              (sum, mi) => sum + mi.minorityResult,
                              0
                            )
                          )}{" "}
                          kr
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                ) : (
                  <p className="text-gray-500 text-sm">
                    Inga minoritetsägare. Alla dotterbolag ägs till 100%.
                  </p>
                )}
              </div>
            </div>

            {/* Note about eliminations */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-2 flex items-center gap-2">
                <FileText size={16} />
                Automatiska elimineringar
              </h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>- Koncerninterna fordringar och skulder (konto 1660/2860)</li>
                <li>- Koncernintern försäljning och inköp</li>
                <li>- Utdelningar inom koncernen</li>
                <li>- Moderbolagets innehav i dotterbolag</li>
              </ul>
              <p className="text-xs text-gray-500 mt-2">
                Dessa elimineringar beräknas automatiskt baserat på SIE-data vid generering av koncernredovisningen.
              </p>
            </div>
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-8 pt-4 border-t border-gray-200">
        <button
          type="button"
          onClick={() => (step > 1 ? setStep(step - 1) : onClose?.())}
          className="btn btn-secondary"
        >
          {step === 1 ? "Avbryt" : "Tillbaka"}
        </button>

        {step < 3 ? (
          <button
            type="button"
            onClick={() => setStep(step + 1)}
            disabled={!isStepValid(step)}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Fortsätt
          </button>
        ) : (
          <button
            type="button"
            onClick={() => {
              // Save consolidation data to report
              // In real implementation, call API to save
              onClose?.();
            }}
            className="btn btn-primary"
          >
            Spara koncernuppgifter
          </button>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// SUBSIDIARY CARD COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

interface SubsidiaryCardProps {
  subsidiary: SubsidiaryData;
  isExpanded: boolean;
  onToggle: () => void;
  onUpdate: (updates: Partial<SubsidiaryData>) => void;
  onRemove: () => void;
  onSieDrop: (files: File[]) => void;
  showAcquisitionFields: boolean;
}

function SubsidiaryCard({
  subsidiary,
  isExpanded,
  onToggle,
  onUpdate,
  onRemove,
  onSieDrop,
  showAcquisitionFields,
}: SubsidiaryCardProps) {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onSieDrop,
    accept: {
      "text/plain": [".se", ".si", ".sie"],
    },
    multiple: false,
  });

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50 ${
          subsidiary.sieFileUploaded ? "bg-green-50" : "bg-white"
        }`}
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              subsidiary.sieFileUploaded
                ? "bg-green-100 text-green-600"
                : "bg-gray-100 text-gray-400"
            }`}
          >
            {subsidiary.sieFileUploaded ? (
              <CheckCircle size={16} />
            ) : (
              <Building2 size={16} />
            )}
          </div>
          <div>
            <p className="font-medium text-gray-900">
              {subsidiary.name || "Nytt dotterbolag"}
            </p>
            {subsidiary.organizationNumber && (
              <p className="text-xs text-gray-500">{subsidiary.organizationNumber}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {subsidiary.ownershipPercentage !== undefined && (
            <span className="text-sm text-gray-500">{subsidiary.ownershipPercentage}%</span>
          )}
          {isExpanded ? (
            <ChevronUp size={16} className="text-gray-400" />
          ) : (
            <ChevronDown size={16} className="text-gray-400" />
          )}
        </div>
      </div>

      {/* Content */}
      {isExpanded && (
        <div className="px-4 py-4 border-t border-gray-100 space-y-4">
          {/* Basic info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Dotterbolagets namn *
              </label>
              <input
                type="text"
                value={subsidiary.name}
                onChange={(e) => onUpdate({ name: e.target.value })}
                className="input w-full"
                placeholder="AB Dotterbolag"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Organisationsnummer *
              </label>
              <input
                type="text"
                value={subsidiary.organizationNumber}
                onChange={(e) => onUpdate({ organizationNumber: e.target.value })}
                className="input w-full"
                placeholder="556xxx-xxxx"
              />
            </div>
          </div>

          {/* SIE upload */}
          {!showAcquisitionFields && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                SIE-fil *
              </label>
              {subsidiary.sieFileUploaded ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="text-sm text-green-800">
                      {subsidiary.sieFileName}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      onUpdate({
                        sieFileUploaded: false,
                        sieFileName: undefined,
                        parsedData: undefined,
                      })
                    }
                    className="text-sm text-red-600 hover:text-red-700"
                  >
                    Ta bort
                  </button>
                </div>
              ) : (
                <div
                  {...getRootProps()}
                  className={`p-6 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors ${
                    isDragActive
                      ? "border-indigo-400 bg-indigo-50"
                      : "border-gray-300 hover:border-gray-400"
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload size={24} className="mx-auto mb-2 text-gray-400" />
                  <p className="text-sm text-gray-600">
                    {isDragActive
                      ? "Släpp filen här..."
                      : "Dra och släpp SIE-fil, eller klicka för att välja"}
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    Stöder .se, .si och .sie filer
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Acquisition fields (step 2) */}
          {showAcquisitionFields && (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Förvärvsdatum *
                  </label>
                  <input
                    type="date"
                    value={subsidiary.acquisitionDate || ""}
                    onChange={(e) => onUpdate({ acquisitionDate: e.target.value })}
                    className="input w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Anskaffningsvärde (kr) *
                  </label>
                  <input
                    type="number"
                    value={subsidiary.acquisitionCost || ""}
                    onChange={(e) =>
                      onUpdate({ acquisitionCost: Number(e.target.value) || undefined })
                    }
                    className="input w-full"
                    placeholder="0"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Eget kapital vid förvärv (kr) *
                  </label>
                  <input
                    type="number"
                    value={subsidiary.equityAtAcquisition || ""}
                    onChange={(e) =>
                      onUpdate({ equityAtAcquisition: Number(e.target.value) || undefined })
                    }
                    className="input w-full"
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Dotterbolagets eget kapital vid förvärvstidpunkten
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ägarandel (%) *
                  </label>
                  <input
                    type="number"
                    min="50.1"
                    max="100"
                    step="0.1"
                    value={subsidiary.ownershipPercentage || ""}
                    onChange={(e) =>
                      onUpdate({ ownershipPercentage: Number(e.target.value) || undefined })
                    }
                    className="input w-full"
                    placeholder="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Måste vara &gt; 50% för att klassas som dotterbolag
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Rösträttsandel (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    step="0.1"
                    value={subsidiary.votingRightsPercentage || ""}
                    onChange={(e) =>
                      onUpdate({ votingRightsPercentage: Number(e.target.value) || undefined })
                    }
                    className="input w-full"
                    placeholder="100"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Lämna tomt om samma som ägarandel
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Avskrivningstid goodwill (år)
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={subsidiary.goodwillUsefulLife || 5}
                    onChange={(e) =>
                      onUpdate({ goodwillUsefulLife: Number(e.target.value) || 5 })
                    }
                    className="input w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    K3: Max 10 år, motivering krävs om &gt; 5 år
                  </p>
                </div>
              </div>

              {(subsidiary.goodwillUsefulLife || 5) > 5 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex gap-2">
                    <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-800">
                      Avskrivningstid för goodwill överstiger 5 år. Enligt K3 kap 18.20 krävs
                      motivering för längre avskrivningstid. Ange motivering i noten för goodwill.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Remove button */}
          <div className="flex justify-end pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onRemove}
              className="flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
            >
              <Trash2 size={14} />
              Ta bort dotterbolag
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
